import { CardSkeleton } from "#web/components/common/CardSkeleton";
import { CustomCard } from "#web/components/common/CustomCard";
import { makeShop, ShopQuery } from "#web/components/shop/queries/shopQuery";
import { VerifyCheckoutExtensionInstalledQuery } from "#web/components/shop/queries/verifyCheckoutExtensionInstalledQuery";
import { routeConfig } from "#web/configs/route";
import { i18nLike } from "#web/services/i18nLike";
import { fetchTinybird, parsePipeResult } from "#web/services/tinybird";
import { toyyyyMMdd } from "#web/utils/dateFormatter";
import { getCachedDateOfNow } from "#web/utils/getCachedDateOfNow";
import { sumCountFromData } from "#web/utils/sumCountFromData";
import { Badge, Banner, BlockStack, Box, Grid, Link, Text } from "@shopify/polaris";
import { ShieldCheckMarkIcon } from "@shopify/polaris-icons";
import { LineChart, PolarisVizProvider } from "@shopify/polaris-viz";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import * as v from "valibot";

const en = {
  blockingChart: {
    blocked: "Blocked",
    checkoutExtensionDisconnected: {
      instructions: "Enable the Checkout Extension on your checkout settings page.",
      seeInstructions: "See Instructions",
      title: "Checkout Extension disconnected",
    },
    primaryAction: "View",
    title: "Blocked Codes",
    tracked: "Tracked",
  },
  error: {
    description: "Please try again. If the issue persists, please contact support.",
    title: "There was an error loading the extension data",
  },
  extensionAnalyticsChart: {
    title: "Visitors with Coupon Extensions",
    upgradeBanner: "Upgrade your plan to protect against coupon extensions",
  },
};

export const DashboardBlockingMetricsQuery = ({
  endDate,
  shopId,
  startDate,
}: {
  endDate: string;
  shopId: string | undefined;
  startDate: string;
}) =>
  queryOptions({
    enabled: !!shopId,
    queryFn: () => {
      const DashboardBlockingMetricsData = v.array(
        v.object({
          blocked_count: v.number(),
          unique_code_count: v.number(),
        }),
      );
      return fetchTinybird("dashboard_blocking_metrics", { endDate, shopId: shopId!, startDate }).then(
        parsePipeResult(DashboardBlockingMetricsData),
      );
    },
    queryKey: ["dashboard_blocking_metrics", shopId, startDate, endDate],
  });

const DashboardExtensionMetricsQuery = ({
  endDate,
  myshopifyDomain,
  startDate,
}: {
  endDate: string;
  myshopifyDomain: string;
  startDate: string;
}) =>
  queryOptions({
    enabled: !!myshopifyDomain,
    queryFn: () => {
      const DashboardExtensionMetricsData = v.array(
        v.object({
          checkout_started: v.number(),
          extension_detected: v.number(),
        }),
      );
      return fetchTinybird("dashboard_extension_metrics", { endDate, shopId: myshopifyDomain, startDate }).then(
        parsePipeResult(DashboardExtensionMetricsData),
      );
    },
    queryKey: ["dashboard_extension_metrics", myshopifyDomain, startDate, endDate],
  });

export const ExtensionsCard = () => {
  const t = i18nLike(en);
  const { data: shopData, isError, isFetching } = useQuery(ShopQuery());
  const shop = shopData ? makeShop(shopData) : null;

  if (isError) {
    return (
      <Banner title={t("error.title")} tone="critical">
        {t("error.description")}
      </Banner>
    );
  }

  if (isFetching) {
    return <CardSkeleton />;
  }

  if (shop?.subscribedTo("blocking")) {
    return <BlockingChart />;
  }

  return <ExtensionAnalyticsChart />;
};

const BlockingChart = () => {
  const t = i18nLike(en);
  const { data: shopData, error: shopError, isFetching: shopFetching } = useQuery(ShopQuery());
  const shop = makeShop(shopData!);
  const navigate = useNavigate();

  const dates = useMemo(() => {
    const today = getCachedDateOfNow();
    today.setHours(0, 0, 0, 0);
    today.setDate(today.getDate() + 1);

    const startDate = getCachedDateOfNow();
    startDate.setHours(0, 0, 0, 0);
    startDate.setDate(startDate.getDate() - 30);

    return { startDate: toyyyyMMdd(startDate), today: toyyyyMMdd(today) };
  }, []);

  const {
    data: checkoutExtensionData,
    error: checkoutExtensionError,
    isFetching: checkoutExtensionFetching,
  } = useQuery(VerifyCheckoutExtensionInstalledQuery({ shopId: shop.id }));
  const {
    data: dashboardBlockingMetricsData,
    error: dashboardBlockingMetricsError,
    isFetching: dashboardBlockingMetricsFetching,
  } = useQuery(
    DashboardBlockingMetricsQuery({
      endDate: dates.today,
      shopId: shop.id,
      startDate: dates.startDate,
    }),
  );
  const {
    data: dashboardExtensionMetricsData,
    error: dashboardExtensionMetricsError,
    isFetching: dashboardExtensionMetricsFetching,
  } = useQuery(
    DashboardExtensionMetricsQuery({
      endDate: dates.today,
      myshopifyDomain: shop.myshopifyDomain!,
      startDate: dates.startDate,
    }),
  );

  const isLoading =
    shopFetching || checkoutExtensionFetching || dashboardBlockingMetricsFetching || dashboardExtensionMetricsFetching;
  const error = shopError ?? checkoutExtensionError ?? dashboardBlockingMetricsError ?? dashboardExtensionMetricsError;

  function GridStatCell({ title, value }: { title: string; value: number | string }) {
    return (
      <Grid.Cell columnSpan={{ sm: 3, xs: 1 }}>
        <Box background="bg-surface-secondary" borderRadius="200" padding="300">
          <BlockStack gap="150">
            <Text as="span" variant="headingSm">
              {title}
            </Text>
            {value === 0 ? (
              <Badge>Optimizing for your store...</Badge>
            ) : (
              <Text as="span" variant="headingLg">
                {value}
              </Text>
            )}
          </BlockStack>
        </Box>
      </Grid.Cell>
    );
  }

  if (error) {
    console.error(error);
    return (
      <Banner title={t("error.title")} tone="critical">
        {t("error.description")}
      </Banner>
    );
  }

  return (
    <CustomCard
      buttonText={t("blockingChart.primaryAction")}
      icon={ShieldCheckMarkIcon}
      loading={isLoading}
      onAction={() => {
        navigate(routeConfig.extensions["/"]);
      }}
      title={t("blockingChart.title")}
    >
      {!checkoutExtensionData?.installed && (
        <Box paddingBlock="200">
          <Banner title="Checkout Extension disconnected" tone="warning">
            {t("blockingChart.checkoutExtensionDisconnected.instructions")}{" "}
            <Link target="_blank" url="https://www.keepcart.co/extension-blocking-newcheckout">
              {t("blockingChart.checkoutExtensionDisconnected.seeInstructions")}
            </Link>
          </Banner>
        </Box>
      )}
      <Box paddingBlockStart="300">
        <Grid columns={{ sm: 4, xs: 1 }}>
          <GridStatCell
            title="Checkout w/ extension"
            value={dashboardExtensionMetricsData?.data[0]?.checkout_started ?? 0}
          />
          <GridStatCell
            title="Extensions detected"
            value={dashboardExtensionMetricsData?.data[0]?.extension_detected ?? 0}
          />
          <GridStatCell title="Blocked codes" value={dashboardBlockingMetricsData?.data[0]?.blocked_count ?? 0} />
          <GridStatCell
            title="Unique codes blocked"
            value={dashboardBlockingMetricsData?.data[0]?.unique_code_count ?? 0}
          />
        </Grid>
      </Box>
    </CustomCard>
  );
};

const ExtensionAnalyticsChartQuery = ({
  endDate,
  shopId,
  startDate,
}: {
  endDate: string;
  shopId: string;
  startDate: string;
}) =>
  queryOptions({
    queryFn: () => {
      const ExtensionAnalyticsChartData = v.array(
        v.object({
          key: v.string(),
          value: v.number(),
        }),
      );
      return fetchTinybird("extension_analytics_chart", { endDate, shopId, startDate }).then(
        parsePipeResult(ExtensionAnalyticsChartData),
      );
    },
    queryKey: ["extension_analytics_chart", shopId, startDate, endDate],
  });

const ExtensionAnalyticsChart = () => {
  const t = i18nLike(en);
  const { data: shopData, isError: shopError, isFetching: shopFetching } = useQuery(ShopQuery());
  const shop = makeShop(shopData!);

  const dates = useMemo(() => {
    const dateOf31DaysAgo = getCachedDateOfNow();
    dateOf31DaysAgo.setDate(dateOf31DaysAgo.getDate() - 31);

    const startDate = dateOf31DaysAgo.toISOString().split("T")[0]!;
    const endDate = getCachedDateOfNow().toISOString().split("T")[0]!;
    return { endDate, startDate };
  }, []);

  const {
    data: extensionAnalyticsData,
    isError: extensionAnalyticsError,
    isFetching: extensionAnalyticsFetching,
  } = useQuery(
    ExtensionAnalyticsChartQuery({
      endDate: dates.endDate,
      shopId: shop.id,
      startDate: dates.startDate,
    }),
  );

  const blockingCount = useMemo(
    () => (extensionAnalyticsData?.data ? sumCountFromData(extensionAnalyticsData.data) : 0),
    [extensionAnalyticsData],
  );

  const isLoading = shopFetching || extensionAnalyticsFetching;
  const isError = shopError || extensionAnalyticsError;

  if (isError) {
    return (
      <Banner title={t("error.title")} tone="critical">
        {t("error.description")}
      </Banner>
    );
  }
  return (
    <CustomCard icon={ShieldCheckMarkIcon} loading={isLoading} title={t("extensionAnalyticsChart.title")}>
      {shop.shopifyPlus && (
        <Box paddingBlock="300">
          <Banner tone="warning">
            <Text as="span" fontWeight="regular" variant="headingMd">
              {t("extensionAnalyticsChart.upgradeBanner")}
            </Text>
          </Banner>
        </Box>
      )}
      <Box borderRadius="200" padding="300">
        <Text as="p" variant="headingXl">
          {blockingCount}
        </Text>
      </Box>
      <PolarisVizProvider>
        <LineChart
          data={[
            {
              data: extensionAnalyticsData?.data ?? [],
            },
          ]}
          showLegend={false}
          theme="Light"
        />
      </PolarisVizProvider>
    </CustomCard>
  );
};
